// /admin/locations — Ring App Store locations admin.
//
// Auto-discovered from Ring; admins rename rows via inline
// <LocationRenameInput> with optimistic-concurrency PATCH (412 →
// "someone else updated, refresh"). Displays display_name source so the
// admin can see which sites still have auto-generated names.

import { useEffect, useState } from "react";
import { ApiError } from "@/api";
import { locationsClient } from "@/api/locationsClient";
import { LocationRenameInput } from "@/components/LocationRenameInput";
import { useRole } from "@/role";
import type { Location } from "@/types/location";

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function statusBadge(status: Location["status"]): JSX.Element {
  switch (status) {
    case "active":
      return <span className="reb-badge reb-badge--success">Active</span>;
    case "removed":
      return <span className="reb-badge reb-badge--danger">Removed</span>;
    case "pending":
    default:
      return <span className="reb-badge reb-badge--warning">Pending</span>;
  }
}

export function LocationsPage(): JSX.Element {
  const { currentRole } = useRole();
  const [items, setItems] = useState<Location[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(
    null,
  );

  async function load(signal?: AbortSignal): Promise<void> {
    try {
      const list = await locationsClient.list(signal);
      setItems(list);
      setLoadError(null);
    } catch (err) {
      if (signal?.aborted) return;
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
        return;
      }
      const msg = err instanceof Error ? err.message : "Failed to load locations";
      setLoadError(msg);
    }
  }

  useEffect(() => {
    if (currentRole === null) return;
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [currentRole]);

  async function handleSave(loc: Location, newName: string): Promise<void> {
    setSavingId(loc.ring_location_id);
    setRowError(null);
    try {
      const updated = await locationsClient.rename(loc.ring_location_id, {
        display_name: newName,
        version: loc.version,
      });
      setItems((curr) =>
        curr
          ? curr.map((c) =>
              c.ring_location_id === updated.ring_location_id ? updated : c,
            )
          : curr,
      );
      setEditing(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setRowError({
          id: loc.ring_location_id,
          message:
            "Someone else updated this location. Refresh to see changes.",
        });
      } else {
        const msg = err instanceof Error ? err.message : "Rename failed";
        setRowError({ id: loc.ring_location_id, message: msg });
      }
    } finally {
      setSavingId(null);
    }
  }

  if (forbidden) {
    return (
      <div className="reb-page">
        <div className="reb-card reb-error">
          You do not have permission to view this page (requires{" "}
          <code>tenant:manage</code>).
        </div>
      </div>
    );
  }

  return (
    <div className="reb-page">
      <header className="reb-page__header">
        <div>
          <h1 className="reb-page__title">Locations</h1>
          <p className="reb-page__subtitle">
            Auto-discovered from Ring. Rename any location to give it a friendly
            identifier.
          </p>
        </div>
      </header>

      {loadError && (
        <div className="reb-card reb-error" role="alert">
          {loadError}
        </div>
      )}

      {items === null && !loadError && <p>Loading...</p>}

      {items !== null && items.length === 0 && (
        <div className="reb-card">
          No locations discovered yet. Locations appear after Ring devices report
          in.
        </div>
      )}

      {items !== null && items.length > 0 && (
        <table className="reb-table">
          <thead>
            <tr>
              <th>Display Name</th>
              <th>Status</th>
              <th>Country / State</th>
              <th>First Seen</th>
              <th>Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {items.map((loc) => {
              const isEditing = editing === loc.ring_location_id;
              const err =
                rowError?.id === loc.ring_location_id ? rowError.message : null;
              return (
                <tr key={loc.ring_location_id}>
                  <td>
                    {isEditing ? (
                      <LocationRenameInput
                        initialName={loc.display_name}
                        saving={savingId === loc.ring_location_id}
                        onSave={(name) => handleSave(loc, name)}
                        onCancel={() => {
                          setEditing(null);
                          setRowError(null);
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="reb-button"
                        onClick={() => {
                          setEditing(loc.ring_location_id);
                          setRowError(null);
                        }}
                        aria-label={`Rename ${loc.display_name}`}
                      >
                        {loc.display_name}
                        {loc.display_name_source === "auto" && (
                          <span
                            className="reb-badge reb-badge--warning"
                            style={{ marginLeft: 8 }}
                          >
                            Rename
                          </span>
                        )}
                      </button>
                    )}
                    {err && (
                      <div
                        className="reb-error"
                        role="alert"
                        style={{ marginTop: 4 }}
                      >
                        {err}
                      </div>
                    )}
                  </td>
                  <td>{statusBadge(loc.status)}</td>
                  <td>
                    {[loc.country, loc.state].filter(Boolean).join(" / ") || "-"}
                  </td>
                  <td>{formatDate(loc.first_seen_at)}</td>
                  <td>{formatDate(loc.last_seen_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
